import { Flex, Link, chakra } from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import { useCoinPrice } from 'lib/contexts/CoinPriceContext';
import dayjs from 'lib/date/dayjs';
import useIsMobile from 'lib/hooks/useIsMobile';
import { HOMEPAGE_STATS } from 'stubs/stats';
import Skeleton from 'ui/shared/chakra/Skeleton';
import GasInfoTooltip from 'ui/shared/gas/GasInfoTooltip';
import GasPrice from 'ui/shared/gas/GasPrice';
import TextSeparator from 'ui/shared/TextSeparator';

import GetGasButton from './GetGasButton';

const TopBarStats = () => {
  const isMobile = useIsMobile();
  const [ mounted, setMounted ] = React.useState(false);
  const {
    seedxPrice,
    seedxChangePercent,
    isSeedxPriceLoading,
    formatPrice,
    formatChange,
  } = useCoinPrice();

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const { data, isPlaceholderData, isError, refetch, dataUpdatedAt } = useApiQuery('stats', {
    queryOptions: {
      placeholderData: HOMEPAGE_STATS,
      refetchOnMount: false,
    },
  });

  React.useEffect(() => {
    if (isPlaceholderData || !data?.gas_price_updated_at) {
      return;
    }

    const endDate = dayjs(dataUpdatedAt).add(data.gas_prices_update_in, 'ms');
    const timeout = endDate.diff(dayjs(), 'ms');

    if (timeout <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      refetch();
    }, timeout);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [ isPlaceholderData, data?.gas_price_updated_at, dataUpdatedAt, data?.gas_prices_update_in, refetch ]);

  if (isError) {
    return <div/>;
  }

  return (
    <Flex
      alignItems="center"
      fontSize="xs"
      fontWeight={ 500 }
      suppressHydrationWarning
    >
      { mounted && (seedxPrice > 0 || isSeedxPriceLoading) && (
        <Flex columnGap={ 1 }>
          <Skeleton isLoaded={ !isSeedxPriceLoading }>
            <chakra.span color="text_secondary">Seedx price </chakra.span>
            <span suppressHydrationWarning>
              { isSeedxPriceLoading ? '...' : formatPrice(seedxPrice) }
            </span>
          </Skeleton>
          { !isSeedxPriceLoading && seedxPrice > 0 && (
            <Skeleton isLoaded={ !isSeedxPriceLoading }>
              <chakra.span color={ seedxChangePercent >= 0 ? 'green.500' : 'red.500' } suppressHydrationWarning>
                { formatChange(seedxChangePercent) }
              </chakra.span>
            </Skeleton>
          ) }
        </Flex>
      ) }
      { mounted && !isMobile && data?.secondary_coin_price && config.chain.secondaryCoin.symbol && (
        <Flex columnGap={ 1 } ml={ (seedxPrice > 0 || isSeedxPriceLoading) ? 3 : 0 }>
          <Skeleton isLoaded={ !isPlaceholderData }>
            <chakra.span color="text_secondary">{ config.chain.secondaryCoin.symbol } </chakra.span>
            <span suppressHydrationWarning>${ Number(data.secondary_coin_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 }) }</span>
          </Skeleton>
        </Flex>
      ) }
      { (seedxPrice > 0 || isSeedxPriceLoading) && config.features.gasTracker.isEnabled && <TextSeparator color="divider"/> }
      { data?.gas_prices && data.gas_prices.average !== null && config.features.gasTracker.isEnabled && (
        <>
          <Skeleton isLoaded={ !isPlaceholderData }>
            <chakra.span color="text_secondary">Gas </chakra.span>
            <GasInfoTooltip data={ data } dataUpdatedAt={ dataUpdatedAt } placement={ !data?.coin_price ? 'bottom-start' : undefined }>
              <Link>
                <GasPrice data={ data.gas_prices.average }/>
              </Link>
            </GasInfoTooltip>
          </Skeleton>
          { !isPlaceholderData && <GetGasButton/> }
        </>
      ) }
    </Flex>
  );
};

export default React.memo(TopBarStats);
